import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../lib/cn';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface ToastOptions {
  title: string;
  /**
   * The body. When reporting an API failure pass `userMessage(error)` — it applies
   * the 404 disclosure rule and shows a 409's reason verbatim.
   */
  description?: ReactNode;
  tone?: ToastTone;
  /** Milliseconds. Pass 0 to require a manual dismissal. */
  duration?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastRecord extends ToastOptions {
  id: string;
}

interface ToastContextValue {
  toast: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const tones: Record<ToastTone, string> = {
  info: 'border-info/30 bg-info-subtle text-ink',
  success: 'border-success/30 bg-success-subtle text-ink',
  warning: 'border-warning/30 bg-warning-subtle text-ink',
  danger: 'border-danger/30 bg-danger-subtle text-ink',
};

const DEFAULT_DURATIONS: Record<ToastTone, number> = {
  info: 5000,
  success: 4000,
  // Warnings and failures stay until dismissed. A message about a refused payment
  // verification vanishing after four seconds is how people miss it.
  warning: 0,
  danger: 0,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = `toast-${Math.random().toString(36).slice(2)}`;
      const tone = options.tone ?? 'info';
      const duration = options.duration ?? DEFAULT_DURATIONS[tone];
      setToasts((current) => [...current, { ...options, tone, id }]);
      if (duration > 0) setTimeout(() => dismiss(id), duration);
      return id;
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            // `polite` rather than `assertive`: a toast should not interrupt someone
            // mid-sentence in a form. Anything that must interrupt is a dialog.
            aria-live="polite"
            aria-atomic="false"
            className="pointer-events-none fixed bottom-0 right-0 z-[60] flex w-full max-w-sm flex-col gap-2 p-4"
          >
            {toasts.map((item) => (
              <div
                key={item.id}
                className={cn(
                  'pointer-events-auto animate-scale-in rounded-lg border p-3 shadow-lg',
                  tones[item.tone ?? 'info'],
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-semibold">{item.title}</p>
                    {item.description && (
                      <p className="mt-0.5 text-sm text-ink-muted">{item.description}</p>
                    )}
                    {item.action && (
                      <button
                        type="button"
                        onClick={() => {
                          item.action?.onClick();
                          dismiss(item.id);
                        }}
                        className="mt-1.5 text-sm font-semibold text-primary underline-offset-2 hover:underline"
                      >
                        {item.action.label}
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => dismiss(item.id)}
                    aria-label="Dismiss"
                    className="-mr-1 -mt-1 rounded p-1 text-ink-subtle hover:text-ink"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M4 4l8 8M12 4l-8 8" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside <ToastProvider>.');
  return context;
}
